import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { BarChart3, FileText, Files, LayoutTemplate, Settings, ShieldBan, Activity, LogOut, Menu, X, ExternalLink, Landmark, Receipt, CalendarDays, Bot, Server } from "lucide-react";
import { api, getToken, setToken } from "../lib/api";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: BarChart3, end: true },
  { to: "/admin/content", label: "Homepage", icon: LayoutTemplate },
  { to: "/admin/exchanges", label: "Exchanges", icon: Landmark },
  { to: "/admin/proofs", label: "Withdraw Proofs", icon: Receipt },
  { to: "/admin/blogs", label: "Blogs", icon: FileText },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/ai-queue", label: "Bulk AI Posts", icon: Bot },
  { to: "/admin/pages", label: "Pages", icon: Files },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/security", label: "Security", icon: ShieldBan },
  { to: "/admin/health", label: "System Health", icon: Activity },
  { to: "/admin/install-guide", label: "Install Guide", icon: Server },
];

const Nav = ({ onNav }) => (
  <nav className="flex flex-col gap-1">
    {LINKS.map((l) => (
      <NavLink key={l.to} to={l.to} end={l.end} onClick={onNav} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#D4AF37]/15 text-[#F3D77A]" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
        <l.icon size={17} /> {l.label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!getToken()) return nav("/admin/login");
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = () => {
    setToken(null);
    nav("/admin/login");
  };

  if (!user) return <div className="grid min-h-screen place-items-center bg-[#0A0A0A] text-zinc-500">Loading…</div>;

  const side = (
    <div className="flex h-full flex-col">
      <div className="mb-8 flex items-baseline gap-1 font-serif"><span className="text-xl font-bold gold-text">DADDYANNA</span><span className="text-xs font-bold text-white">247</span><span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-500">Admin</span></div>
      <Nav onNav={() => setOpen(false)} />
      <div className="mt-auto space-y-1 pt-8">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-zinc-400 hover:text-white"><ExternalLink size={17} /> View site</a>
        <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-zinc-400 hover:text-red-400"><LogOut size={17} /> Logout</button>
        <p className="truncate px-3 pt-2 text-xs text-zinc-600">{user.email}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] font-sans text-white">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-[#D4AF37]/15 bg-[#0C0D12] p-5 lg:block">{side}</aside>
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#D4AF37]/15 bg-[#0C0D12]/95 px-4 backdrop-blur lg:hidden">
        <span className="font-serif font-bold gold-text">DADDYANNA247</span>
        <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu" className="text-[#D4AF37]"><Menu /></button>
      </header>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-[#0C0D12] p-5">
            <button onClick={() => setOpen(false)} className="absolute right-4 top-4 text-zinc-400"><X /></button>
            {side}
          </div>
        </div>
      )}
      <main className="px-4 py-8 sm:px-6 lg:ml-64 lg:px-10"><Outlet /></main>
    </div>
  );
}
